import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Input } from '@heroui/input';
import Trash from '@/icons/Trash';
import { IconChevronDown as ChevronDown, IconChevronUp as ChevronUp } from '@tabler/icons-react';
import TextFieldIcon from '@/icons/TextFieldIcon';
import TextAreaIcon from '@/icons/TextAreaIcon';
import CheckboxIcon from '@/icons/CheckboxIcon';
import SelectIcon from '@/icons/SelectIcon';
import { card, label as labelCls } from '@/components/ui/design';
import { Select, SelectItem } from '@/components/ui/AppSelect';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const inputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: fieldText };
const inputErrCls = { inputWrapper: `h-14 ${fieldWrap} ring-2 ring-red-400 dark:ring-red-400/70`, input: fieldText };
const nativeField = 'w-full rounded-2xl bg-grey99 px-4 py-3 text-base text-grey10 ring-1 ring-black/[0.04] transition-shadow placeholder:text-grey60 focus:outline-none focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60';
const errorBox = 'rounded-2xl bg-red-50 p-4 ring-1 ring-red-200/60 dark:bg-red-500/10 dark:ring-red-500/20';
const roundBtn = 'flex h-9 w-9 items-center justify-center rounded-full bg-grey99 text-grey20 transition-colors hover:bg-grey95 disabled:opacity-40 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10';
const roundBtnDanger = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100 disabled:opacity-40 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20';
const subHeading = 'text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white';

export interface FormQuestion {
    id: string;
    type: 'short-answer' | 'long-answer' | 'checkbox' | 'select';
    title: string;
    description?: string;
    required: boolean;
    options?: string[]; // For checkbox and select types
}

export interface FormData {
    id?: string;
    title: string;
    description?: string;
    questions: FormQuestion[];
    organizerId: string;
}

interface FormGeneratorProps {
    organizerId: string;
    onSave?: (formData: FormData) => void;
    initialData?: FormData;
    hideAddQuestions?: boolean;
}

export interface FormGeneratorRef {
    addQuestion: (type: FormQuestion['type']) => void;
    save: () => void;
    isPreview: boolean;
    togglePreview: () => void;
    isSaving: boolean;
}

const FormGenerator = forwardRef<FormGeneratorRef, FormGeneratorProps>(({ organizerId, onSave, initialData, hideAddQuestions = false }, ref) => {
    const [formTitle, setFormTitle] = useState(initialData?.title || '');
    const [formDescription, setFormDescription] = useState(initialData?.description || '');
    const [questions, setQuestions] = useState<FormQuestion[]>(initialData?.questions || []);
    const [showPreview, setShowPreview] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
    const [isSaving, setIsSaving] = useState(false);

    // Use refs to store latest values for validation (avoids stale closure issues)
    const formTitleRef = useRef(formTitle);
    const questionsRef = useRef(questions);
    const formDescriptionRef = useRef(formDescription);

    // Keep refs in sync with state
    useEffect(() => {
        formTitleRef.current = formTitle;
    }, [formTitle]);
    useEffect(() => {
        questionsRef.current = questions;
    }, [questions]);
    useEffect(() => {
        formDescriptionRef.current = formDescription;
    }, [formDescription]);

    // Track the last initialData ID to detect when we're switching lists
    const lastInitialDataIdRef = useRef(initialData?.id);
    const isInitialMountRef = useRef(true);

    // Reset form when initialData changes (when switching organizations or lists)
    // Only reset if the ID actually changed (switching lists), not just data updates
    useEffect(() => {
        const currentId = initialData?.id;
        const lastId = lastInitialDataIdRef.current;

        // On initial mount, always initialize with initialData if provided
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            if (initialData) {
                console.log('FormGenerator: Initial mount, initializing with initialData');
                const newTitle = initialData.title || '';
                const newDescription = initialData.description || '';
                const newQuestions = initialData.questions || [];
                setFormTitle(newTitle);
                formTitleRef.current = newTitle;
                setFormDescription(newDescription);
                formDescriptionRef.current = newDescription;
                setQuestions(newQuestions);
                questionsRef.current = newQuestions;
                lastInitialDataIdRef.current = currentId;
            }
            return;
        }

        // Only reset if we're switching to a different list (ID changed)
        // This prevents losing unsaved changes when the same list's data refetches
        if (currentId !== lastId) {
            console.log('FormGenerator: initialData ID changed, resetting form', { from: lastId, to: currentId });
            const newTitle = initialData?.title || '';
            const newDescription = initialData?.description || '';
            const newQuestions = initialData?.questions || [];
            setFormTitle(newTitle);
            formTitleRef.current = newTitle;
            setFormDescription(newDescription);
            formDescriptionRef.current = newDescription;
            setQuestions(newQuestions);
            questionsRef.current = newQuestions;
            setShowPreview(false);
            setErrors({});
            setTouched({});
            lastInitialDataIdRef.current = currentId;
        }
        // If same ID, don't reset - preserve user's unsaved changes
    }, [initialData]);


    const addQuestion = (type: FormQuestion['type']) => {
        const newQuestion: FormQuestion = {
            id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type,
            title: '',
            required: false,
            options: type === 'checkbox' || type === 'select' ? [''] : undefined
        };
        setQuestions(prev => [...prev, newQuestion]);

        // Clear questions error if it exists
        setErrors(prev => {
            if (prev.questions) {
                const newErrors = { ...prev };
                delete newErrors.questions;
                return newErrors;
            }
            return prev;
        });
    };

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        addQuestion,
        save: handleSave,
        isPreview: showPreview,
        togglePreview: () => setShowPreview(prev => !prev),
        isSaving: isSaving
    }), [isSaving]);

    const updateQuestion = (id: string, updates: Partial<FormQuestion>) => {
        setQuestions(prev => prev.map(q => q.id === id ? { ...q, ...updates } : q));
    };

    const deleteQuestion = (id: string) => {
        setQuestions(questions.filter(q => q.id !== id));
    };

    const moveQuestion = (id: string, direction: 'up' | 'down') => {
        const index = questions.findIndex(q => q.id === id);
        if (index === -1) return;

        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= questions.length) return;

        const newQuestions = [...questions];
        [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
        setQuestions(newQuestions);
    };

    const addOption = (questionId: string) => {
        const question = questions.find(q => q.id === questionId);
        if (question && question.options) {
            const newOptions = [...question.options, ``];
            updateQuestion(questionId, { options: newOptions });
        }
    };

    const updateOption = (questionId: string, optionIndex: number, value: string) => {
        const question = questions.find(q => q.id === questionId);
        if (question && question.options) {
            const newOptions = [...question.options];
            newOptions[optionIndex] = value;
            updateQuestion(questionId, { options: newOptions });

            // Clear options error if all options are now valid
            const touchedKey = `question_${questionId}_options`;
            if (touched[touchedKey]) {
                const hasEmptyOptions = newOptions.some(option => !option.trim());
                const uniqueOptions = new Set(newOptions.filter(option => option.trim()));
                const hasDuplicates = uniqueOptions.size !== newOptions.length;

                if (!hasEmptyOptions && !hasDuplicates && newOptions.length > 0) {
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[`question_${questionId}_options`];
                        return newErrors;
                    });
                }
            }
        }
    };

    const deleteOption = (questionId: string, optionIndex: number) => {
        const question = questions.find(q => q.id === questionId);
        if (question && question.options && question.options.length > 1) {
            const newOptions = question.options.filter((_, index) => index !== optionIndex);
            updateQuestion(questionId, { options: newOptions });
        }
    };

    // Validation functions - use refs to always get latest values
    const validateForm = (): boolean => {
        // Use refs to get the latest values (avoids stale closure issues)
        const currentTitle = formTitleRef.current;
        const currentQuestions = questionsRef.current;

        const newErrors: { [key: string]: string } = {};

        // Validate form title
        if (!currentTitle.trim()) {
            newErrors.formTitle = 'Form title is required';
        }

        // Validate questions
        if (currentQuestions.length === 0) {
            newErrors.questions = 'At least one question is required';
        }

        // Validate each question
        currentQuestions.forEach((question, index) => {
            const questionKey = `question_${question.id}`;

            if (!question.title.trim()) {
                newErrors[`${questionKey}_title`] = `Question ${index + 1} title is required`;
            }

            // Validate options for checkbox and select questions
            if ((question.type === 'checkbox' || question.type === 'select')) {
                if (!question.options || question.options.length === 0) {
                    newErrors[`${questionKey}_options`] = `Question ${index + 1} must have at least one option`;
                } else {
                    // Check for empty options
                    const hasEmptyOptions = question.options.some(option => !option.trim());
                    if (hasEmptyOptions) {
                        newErrors[`${questionKey}_options`] = `Question ${index + 1} has empty options`;
                    }

                    // Check for duplicate options
                    const uniqueOptions = new Set(question.options.filter(option => option.trim()));
                    if (uniqueOptions.size !== question.options.length) {
                        newErrors[`${questionKey}_options`] = `Question ${index + 1} has duplicate options`;
                    }
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        // Use refs to get the latest values (avoids stale closure issues)
        const currentTitle = formTitleRef.current;
        const currentQuestions = questionsRef.current;
        const currentDescription = formDescriptionRef.current;

        // Mark all fields as touched
        setTouched(prev => ({
            ...prev,
            formTitle: true,
            questions: true,
            ...currentQuestions.reduce((acc, question) => {
                acc[`question_${question.id}_title`] = true;
                if (question.type === 'checkbox' || question.type === 'select') {
                    acc[`question_${question.id}_options`] = true;
                }
                return acc;
            }, {} as { [key: string]: boolean })
        }));

        // Validate with current values from refs
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        try {
            const formData: FormData = {
                id: initialData?.id,
                title: currentTitle,
                description: currentDescription,
                questions: currentQuestions,
                organizerId
            };

            // Call onSave and wait if it returns a promise
            const result = onSave?.(formData);
            if (result && typeof result === 'object' && 'then' in result) {
                await (result as Promise<any>);
            } else {
                // If not a promise, add a small delay for UX
                await new Promise(resolve => setTimeout(resolve, 300));
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Real-time validation
    const handleFormTitleChange = (value: string) => {
        setFormTitle(value);
        if (touched.formTitle) {
            if (!value.trim()) {
                setErrors(prev => ({ ...prev, formTitle: 'Form title is required' }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.formTitle;
                    return newErrors;
                });
            }
        }
    };

    const handleQuestionTitleChange = (questionId: string, value: string) => {
        updateQuestion(questionId, { title: value });
        const touchedKey = `question_${questionId}_title`;
        if (touched[touchedKey]) {
            if (!value.trim()) {
                setErrors(prev => ({ ...prev, [`question_${questionId}_title`]: 'Question title is required' }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`question_${questionId}_title`];
                    return newErrors;
                });
            }
        }
    };

    const getQuestionTypeLabel = (type: FormQuestion['type']): React.ReactNode => {
        const typeMap: Record<FormQuestion['type'], { icon: React.ReactNode; label: string }> = {
            'short-answer': { icon: <TextFieldIcon />, label: 'Text Field' },
            'long-answer': { icon: <TextAreaIcon />, label: 'Text Area' },
            'checkbox': { icon: <CheckboxIcon />, label: 'Checkbox' },
            'select': { icon: <SelectIcon />, label: 'Select' }
        };
        const typeInfo = typeMap[type] || { icon: null, label: type };
        return (
            <span className="flex items-center gap-2">
                {typeInfo.icon}
                <span>{typeInfo.label}</span>
            </span>
        );
    };

    const renderQuestionEditor = (question: FormQuestion, index: number) => {
        return (
            <div key={question.id} className={`${card} p-4 sm:p-5`}>
                <div className="mb-4 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        {/* <span className="text-sm font-medium text-gray-500 dark:text-nightMuted">Question {index + 1}</span> */}
                        <span className="inline-flex items-center rounded-full bg-grey99 px-3 py-1.5 text-sm font-semibold text-grey10 dark:bg-white/[0.06] dark:text-white">
                            {getQuestionTypeLabel(question.type)}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => moveQuestion(question.id, 'up')}
                            disabled={index === 0}
                            className={roundBtn}
                        >
                            <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => moveQuestion(question.id, 'down')}
                            disabled={index === questions.length - 1}
                            className={roundBtn}
                        >
                            <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => deleteQuestion(question.id)}
                            className={roundBtnDanger}
                        >
                            <Trash width={18} height={18} />
                        </button>
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <Input
                            placeholder="Question title"
                            value={question.title}
                            onChange={(e) => handleQuestionTitleChange(question.id, e.target.value)}
                            onBlur={() => setTouched(prev => ({ ...prev, [`question_${question.id}_title`]: true }))}
                            className={`w-full ${errors[`question_${question.id}_title`] ? 'border-red-500' : ''}`}
                            classNames={errors[`question_${question.id}_title`] ? inputErrCls : inputCls}
                        />
                        {errors[`question_${question.id}_title`] && (
                            <div className="mt-1.5 text-xs font-medium text-red-500 dark:text-red-400">{errors[`question_${question.id}_title`]}</div>
                        )}
                    </div>


                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id={`required-${question.id}`}
                            checked={question.required}
                            onChange={(e) => updateQuestion(question.id, { required: e.target.checked })}
                            className="h-4 w-4 rounded border-grey80 text-primary focus:ring-primary/40 dark:border-white/20 dark:accent-nightAccent"
                        />
                        <label htmlFor={`required-${question.id}`} className="text-sm font-medium text-grey20 dark:text-nightTextSecondary">
                            Required
                        </label>
                    </div>

                    {(question.type === 'checkbox' || question.type === 'select') && (
                        <div className="space-y-2">
                            <div className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Options: *</div>
                            {question.options?.map((option, optionIndex) => (
                                <div key={optionIndex} className="flex items-center gap-2">
                                    <Input
                                        placeholder={`Option ${optionIndex + 1}`}
                                        value={option}
                                        onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                                        onBlur={() => setTouched(prev => ({ ...prev, [`question_${question.id}_options`]: true }))}
                                        className={`flex-1 ${errors[`question_${question.id}_options`] ? 'border-red-500' : ''}`}
                                        classNames={errors[`question_${question.id}_options`] ? inputErrCls : inputCls}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => deleteOption(question.id, optionIndex)}
                                        disabled={question.options!.length <= 1}
                                        className={roundBtnDanger}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                            {errors[`question_${question.id}_options`] && (
                                <div className="text-xs font-medium text-red-500 dark:text-red-400">{errors[`question_${question.id}_options`]}</div>
                            )}
                            <button
                                type="button"
                                onClick={() => addOption(question.id)}
                                className="inline-flex h-9 items-center rounded-full px-4 text-sm font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]"
                            >
                                + Add option
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderPreview = () => {
        return (
            <div className="space-y-6">
                <div className="text-center">
                    <h2 className="mb-2 text-[clamp(24px,3vw,32px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">{formTitle}</h2>
                    {formDescription && (
                        <p className="text-base text-grey40 dark:text-nightMuted">{formDescription}</p>
                    )}
                </div>

                {questions.map((question, index) => (
                    <div key={question.id} className="space-y-2">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">
                                {index + 1}. {question.title}
                                {question.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                        </div>

                        {question.type === 'short-answer' && (
                            <Input placeholder="Your answer" className="w-full" classNames={inputCls} />
                        )}

                        {question.type === 'long-answer' && (
                            <textarea
                                placeholder="Your answer"

                                className={`${nativeField} resize-none`}
                                rows={4}
                            />
                        )}

                        {question.type === 'checkbox' && (
                            <div className="space-y-2">
                                {question.options?.map((option, optionIndex) => (
                                    <div key={optionIndex} className="flex items-center gap-2">
                                        <input type="checkbox" className="h-4 w-4 rounded border-grey80 text-primary focus:ring-primary/40 dark:border-white/20 dark:accent-nightAccent" />
                                        <label className="text-sm text-grey20 dark:text-nightTextSecondary">{option}</label>
                                    </div>
                                ))}
                            </div>
                        )}

                        {question.type === 'select' && (
                            <Select aria-label="Select">
                                <SelectItem >Select an option</SelectItem>
                                {question.options?.map((option, optionIndex) => (
                                    <SelectItem key={optionIndex}>{option}</SelectItem>
                                ))}
                            </Select>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Validation Summary */}
            {Object.keys(errors).length > 0 && (
                <div className={errorBox}>
                    <div className="flex items-center gap-2 mb-2">
                        <div className="text-red-600 dark:text-red-400 font-medium text-sm">⚠️ Please fix the following errors:</div>
                    </div>
                    <ul className="text-red-600 dark:text-red-400 text-sm space-y-1">
                        {Object.entries(errors).map(([key, message]) => (
                            <li key={key} className="flex items-start gap-2">
                                <span className="mt-0.5 text-red-400">•</span>
                                <span>{message}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div>
                {/*  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-nightTextSecondary">Form Builder</h3>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setShowPreview(!showPreview)}
                            className="px-4 py-2 text-sm border border-gray-300 dark:border-nightBorder rounded hover:bg-gray-50 dark:hover:bg-white/10"
                        >
                            {showPreview ? 'Edit' : 'Preview'}
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            className="px-4 py-2 text-sm bg-primary40 text-white rounded hover:bg-[#00796b]"
                        >
                            Save Form
                        </button>
                    </div>
                </div> */}

                {!showPreview && (
                    <div className="space-y-4">
                        <div>
                            <label className={labelCls}>Form Title *</label>
                            <Input
                                placeholder="Enter form title"
                                value={formTitle}
                                onChange={(e) => handleFormTitleChange(e.target.value)}
                                onBlur={() => setTouched(prev => ({ ...prev, formTitle: true }))}
                                className={`w-full ${errors.formTitle ? 'border-red-500' : ''}`}
                                classNames={errors.formTitle ? inputErrCls : inputCls}
                            />
                            {errors.formTitle && (
                                <div className="mt-1.5 text-xs font-medium text-red-500 dark:text-red-400">{errors.formTitle}</div>
                            )}
                        </div>
                        <div>
                            <label className={labelCls}>Form Description</label>
                            <textarea
                                placeholder="Enter form description (optional)"
                                value={formDescription}
                                onChange={(e) => setFormDescription(e.target.value)}
                                className={`${nativeField} resize-none`}
                                rows={3}
                            />
                        </div>
                    </div>
                )}
            </div>


            {/* Form Content */}
            {showPreview ? (
                <div className={`${card} p-5 sm:p-6`}>
                    <h4 className={`${subHeading} mb-4`}>Form Preview</h4>
                    {renderPreview()}
                </div>
            ) : (
                <>
                    {/* Add Question Buttons - Only show if not hidden */}
                    {!hideAddQuestions && (
                        <div className={`${card} p-5 sm:p-6`}>
                            <h4 className={`${subHeading} mb-4`}>Add Questions</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <button
                                    type="button"
                                    onClick={() => addQuestion('short-answer')}
                                    className="rounded-2xl bg-grey99 p-4 text-center ring-1 ring-black/[0.04] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                >
                                    <div className="text-2xl mb-2">📝</div>
                                    <div className="text-sm font-semibold text-grey10 dark:text-white">Short Answer</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => addQuestion('long-answer')}
                                    className="rounded-2xl bg-grey99 p-4 text-center ring-1 ring-black/[0.04] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                >
                                    <div className="text-2xl mb-2">📄</div>
                                    <div className="text-sm font-semibold text-grey10 dark:text-white">Long Answer</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => addQuestion('checkbox')}
                                    className="rounded-2xl bg-grey99 p-4 text-center ring-1 ring-black/[0.04] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                >
                                    <div className="text-2xl mb-2">☑️</div>
                                    <div className="text-sm font-semibold text-grey10 dark:text-white">Checkbox</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => addQuestion('select')}
                                    className="rounded-2xl bg-grey99 p-4 text-center ring-1 ring-black/[0.04] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                >
                                    <div className="text-2xl mb-2">📋</div>
                                    <div className="text-sm font-semibold text-grey10 dark:text-white">Select</div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Questions List */}
                    {questions.length > 0 && (
                        <div className="space-y-4">
                            <h4 className={subHeading}>Questions ({questions.length})</h4>
                            {questions.map((question, index) => renderQuestionEditor(question, index))}
                        </div>
                    )}

                    {/* Questions validation error */}
                    {errors.questions && (
                        <div className={errorBox}>
                            <div className="text-red-600 dark:text-red-400 text-sm">{errors.questions}</div>
                        </div>
                    )}

                    {questions.length === 0 && (
                        <div className="rounded-[28px] bg-grey99 px-6 py-12 text-center dark:bg-white/[0.04]">
                            <div className="mb-2 text-lg text-grey60">📝</div>
                            <div className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">No questions added yet</div>
                            <div className="mt-1 text-sm text-grey40 dark:text-nightMuted">Click on the question types above to get started</div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

FormGenerator.displayName = 'FormGenerator';

export default FormGenerator;
